import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { API_BASE } from "../../constants/api";
import ChangeStripeAccount from "./ChangeStripeAccount";

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : "—");

async function call(path, { method = "GET", body } = {}) {
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) {
    const err = new Error(j.error || `Request failed (${r.status})`);
    err.problems = j.problems || null;
    throw err;
  }
  return j;
}

// The platform's Stripe keys. Saving here switches the key every Stripe call
// on the platform account uses (payments service), for all clinics, within a
// minute - so keys are checked first: same Stripe account, same live/test
// mode, enough access.
export default function StripePlatformSettings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [problems, setProblems] = useState(null);
  const [checked, setChecked] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [state, setState] = useState(null);
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [revertTyped, setRevertTyped] = useState("");

  const apply = (j) => {
    setState(j);
    setStripePublishableKey(j.stripePublishableKey || "");
    setStripeSecretKey("");
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      apply(await call("/admin/stripe/platform-settings"));
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const body = () => {
    const b = { stripePublishableKey: stripePublishableKey.trim() };
    if (stripeSecretKey.trim()) b.stripeSecretKey = stripeSecretKey.trim();
    return b;
  };

  // Any edit invalidates an earlier check.
  const edit = (setter) => (e) => {
    setter(e.target.value);
    setChecked(null);
    setProblems(null);
  };

  const check = async () => {
    setBusy("check");
    setError("");
    setProblems(null);
    try {
      const j = await call("/admin/stripe/platform-settings/verify", { method: "POST", body: body() });
      if (j.ok) {
        setChecked(j);
      } else {
        setChecked(null);
        setProblems(j.problems || ["These keys can't be used."]);
      }
    } catch (e) {
      setError(e.message || "Check failed");
    } finally {
      setBusy("");
    }
  };

  const run = async (kind) => {
    setConfirm(null);
    setRevertTyped("");
    setBusy(kind);
    setError("");
    setProblems(null);
    try {
      const j =
        kind === "revert"
          ? await call("/admin/stripe/platform-settings/revert", { method: "POST", body: { confirmation: revertTyped.trim() } })
          : await call("/admin/stripe/platform-settings", { method: "PUT", body: body() });
      toast.success(kind === "revert" ? "Previous keys restored" : "Stripe keys switched");
      setChecked(null);
      await load();
      return j;
    } catch (e) {
      if (e.problems) setProblems(e.problems);
      else setError(e.message || "Failed");
      toast.error(kind === "revert" ? "Revert failed" : "Save failed");
    } finally {
      setBusy("");
    }
  };

  const inUse = state?.inUse || {};
  const changed =
    stripeSecretKey.trim() !== "" || stripePublishableKey.trim() !== (state?.stripePublishableKey || "");
  // A publishable key stored here but not in use (saved before keys were
  // checked): it can be confirmed as it is, without editing the field.
  const storedKeyNotInUse =
    !!state?.stripePublishableKey &&
    inUse.publishableKey !== "saved" &&
    stripePublishableKey.trim() === state.stripePublishableKey;

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Toaster position="top-right" />
      <Typography variant="h5" sx={{ mb: 1 }} fontWeight={700}>
        Stripe platform keys
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The keys Acolide uses for its own Stripe account: plans and subscriptions, SMS and email billing, and
        clinics' Stripe Connect. Saving switches them for every clinic within a minute. Only keys for the same
        Stripe account and mode are accepted.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                In use now
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "monospace" }}>
                  {inUse.accountId || "Unknown account"}
                </Typography>
                <Chip size="small" color={inUse.livemode ? "success" : "warning"} label={inUse.livemode ? "Live mode" : "Test mode"} />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  Secret key: <b>{inUse.secretKey === "saved" ? "saved here" : "from the server environment"}</b>
                  {inUse.secretKeyHint ? ` (${inUse.secretKeyHint})` : ""}
                </Typography>
                <Typography variant="body2">
                  Publishable key: <b>{inUse.publishableKey === "saved" ? "saved here" : "from the web app's build"}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last changed {formatWhen(state?.updatedAt)}
                  {state?.updatedBy ? ` by ${state.updatedBy}` : ""}
                </Typography>
              </Stack>
              {state?.canRevert ? (
                <Box sx={{ mt: 2 }}>
                  <Button variant="outlined" color="warning" size="small" disabled={!!busy} onClick={() => setConfirm("revert")}>
                    {busy === "revert" ? "Reverting…" : "Revert to previous keys"}
                  </Button>
                </Box>
              ) : null}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <CardContent>
              <Stack spacing={2.5}>
                <TextField
                  label="Publishable key"
                  value={stripePublishableKey}
                  onChange={edit(setStripePublishableKey)}
                  fullWidth
                  placeholder="pk_live_… or pk_test_…"
                  helperText={
                    storedKeyNotInUse
                      ? "This key is stored but not in use - the web app still uses its own. Check keys, then Save and switch, to use it."
                      : "Leave blank to use the web app's own key."
                  }
                />
                <TextField
                  label="Secret key"
                  type="password"
                  value={stripeSecretKey}
                  onChange={edit(setStripeSecretKey)}
                  fullWidth
                  autoComplete="new-password"
                  placeholder="sk_live_… / rk_live_… (or _test_ on staging)"
                  helperText={
                    inUse.secretKey === "saved"
                      ? "Leave blank to keep the saved key."
                      : "Leave blank to keep using the server environment's key."
                  }
                />

                {problems ? (
                  <Alert severity="error">
                    <b>These keys can't be used:</b>
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                      {problems.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}
                {checked ? (
                  <Alert severity="success">
                    Checked: Stripe account {checked.accountId}, {checked.livemode ? "live" : "test"} mode, with access to
                    plans, customers, subscriptions, Connect and the billing portal.
                  </Alert>
                ) : null}

                <Stack direction="row" spacing={1.5}>
                  <Button variant="outlined" onClick={check} disabled={!!busy || !(changed || storedKeyNotInUse)}>
                    {busy === "check" ? "Checking…" : "Check keys"}
                  </Button>
                  <Button variant="contained" onClick={() => setConfirm("save")} disabled={!!busy || !checked}>
                    {busy === "save" ? "Switching…" : "Save and switch"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <ChangeStripeAccount call={call} inUse={inUse} onChanged={load} busy={busy} setBusy={setBusy} />

          {state?.history?.length ? (
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Recent changes
                </Typography>
                <Stack divider={<Divider flexItem />} spacing={1} sx={{ mt: 1 }}>
                  {state.history.map((h, i) => (
                    <Stack key={`${h.at}-${i}`} direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ minWidth: 170 }} color="text.secondary">
                        {formatWhen(h.at)}
                      </Typography>
                      <Chip size="small" variant="outlined" color={h.ok ? "default" : "error"} label={`${{ revert: "Revert", account_change: "Account change", save: "Save" }[h.action] || h.action}${h.ok ? "" : " refused"}`} />
                      <Typography variant="body2">{h.by || "superadmin"}</Typography>
                      {h.fromAccountId && h.ok ? (
                        <Typography variant="body2" color="text.secondary">
                          {h.fromAccountId} → {h.accountId}
                        </Typography>
                      ) : null}
                      {!h.ok && h.problems?.length ? (
                        <Typography variant="body2" color="text.secondary">
                          {h.problems[0]}
                        </Typography>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </Stack>
      )}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm === "revert" ? "Go back to the previous keys?" : "Switch Stripe keys?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every clinic's plan billing, SMS and email billing and Stripe Connect will use{" "}
            {confirm === "revert" ? "the previous keys" : "these keys"} within a minute. If the old key is revoked in
            Stripe afterwards, only the new one will work.
          </DialogContentText>
          {confirm === "revert" && state?.revertChangesAccount ? (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Alert severity="error">
                This moves the platform back to Stripe account <b>{state.previousAccountId}</b>. Clinics that connected or
                saved billing details in {inUse.accountId} will have to do so again; plan subscriptions started in{" "}
                {inUse.accountId} stay billing there.
              </Alert>
              <Typography variant="body2">
                To confirm, type <b style={{ fontFamily: "monospace" }}>{state.revertConfirmationPhrase}</b>
              </Typography>
              <TextField value={revertTyped} onChange={(e) => setRevertTyped(e.target.value)} fullWidth inputProps={{ spellCheck: false, autoComplete: "off" }} />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirm(null); setRevertTyped(""); }}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm === "revert" ? "warning" : "primary"}
            onClick={() => run(confirm)}
            disabled={confirm === "revert" && state?.revertChangesAccount && revertTyped.trim() !== state.revertConfirmationPhrase}
          >
            {confirm === "revert" ? "Revert" : "Switch keys"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
